import React, { useState } from 'react';
import { Search, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useToolsFilter, ToolTag } from '@/hooks/useToolsFilter';
import { useOrganization } from '@/contexts/OrganizationContext';
import { cn } from '@/lib/utils';

interface ToolsFilterSectionProps {
  selectedToolIds: Set<string>;
  onToggle: (toolId: string) => void;
}

const ToolsFilterSection: React.FC<ToolsFilterSectionProps> = ({
  selectedToolIds,
  onToggle
}) => {
  const { organization } = useOrganization();
  const { allTools, topTools, loading, searchTools } = useToolsFilter(organization?.id);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTools = searchQuery ? searchTools(searchQuery) : allTools;
  
  // Show top 6 tools by default
  const visibleTools = topTools.slice(0, 6);
  
  // Get selected tools that aren't in visible list to show them
  const selectedNotVisible = allTools.filter(
    t => selectedToolIds.has(t.id) && !visibleTools.some(v => v.id === t.id)
  );

  const remainingCount = allTools.length - 6;

  if (loading) {
    return (
      <div className="border-b border-border pb-4">
        <span className="font-semibold text-sm">Tools</span>
        <p className="text-xs text-muted-foreground mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <Collapsible defaultOpen className="border-b border-border pb-4">
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:text-primary transition-colors">
        <span className="font-semibold text-sm">Tools</span>
        <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 space-y-3">
        {/* Search Combobox */}
        <Popover open={isSearchOpen} onOpenChange={setIsSearchOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full justify-start text-muted-foreground font-normal h-8"
            >
              <Search className="mr-2 h-3.5 w-3.5" />
              Search {allTools.length} tools...
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput 
                placeholder="Search tools..." 
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>No tools found.</CommandEmpty>
                <CommandGroup>
                  {filteredTools.slice(0, 15).map(tool => (
                    <CommandItem
                      key={tool.id}
                      value={tool.name}
                      onSelect={() => {
                        onToggle(tool.id);
                        setSearchQuery('');
                      }}
                      className="flex items-center gap-2"
                    >
                      <div className={cn(
                        "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        selectedToolIds.has(tool.id) 
                          ? "bg-primary text-primary-foreground" 
                          : "opacity-50"
                      )}>
                        {selectedToolIds.has(tool.id) && <Check className="h-3 w-3" />}
                      </div>
                      <span className="flex-1 truncate">{tool.name}</span>
                      {tool.resourceCount > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {tool.resourceCount}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Selected tools not in visible list */}
        {selectedNotVisible.length > 0 && (
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Selected</span>
            {selectedNotVisible.map(tool => (
              <div key={tool.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`tool-${tool.id}`}
                  checked={true}
                  onCheckedChange={() => onToggle(tool.id)}
                />
                <label
                  htmlFor={`tool-${tool.id}`}
                  className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                >
                  {tool.name}
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Visible Tools (top 6 by popularity) */}
        {visibleTools.length > 0 && (
          <div className="space-y-1.5">
            {visibleTools.map(tool => (
              <div key={tool.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`tool-top-${tool.id}`}
                  checked={selectedToolIds.has(tool.id)}
                  onCheckedChange={() => onToggle(tool.id)}
                />
                <label
                  htmlFor={`tool-top-${tool.id}`}
                  className="text-sm cursor-pointer flex-1 flex items-center gap-2"
                >
                  {tool.name}
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Show count of additional tools */}
        {remainingCount > 0 && (
          <p className="text-xs text-muted-foreground pt-1">
            + {remainingCount} more
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ToolsFilterSection;
